/**
 * Image Storage Utility using IndexedDB
 * Stores pending/selected images per contact so they show in chat after reload
 */

class ImageStorage {
  constructor() {
    this.dbName = "ContactImagesDB";
    this.version = 1;
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("images")) {
          const store = db.createObjectStore("images", { keyPath: "id", autoIncrement: true });
          store.createIndex("contactId", "contactId", { unique: false });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) await this.init();
    return this.db;
  }

  /**
   * Save images for a contact. Each item: { name, url, uploadedUrl } where url is server path (e.g. /uploads/images/captured/xxx)
   */
  async saveImages(contactId, images) {
    if (!contactId || !Array.isArray(images)) return;
    const toSave = images.filter((img) => img && (img.uploadedUrl || img.url));
    if (toSave.length === 0) return;
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(["images"], "readwrite");
      const store = tx.objectStore("images");
      const index = store.index("contactId");

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);

      const deleteNext = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          for (const img of toSave) {
            const url = img.uploadedUrl || img.url;
            if (!url) continue;
            const normalized = url.startsWith("http") || url.startsWith("/") ? url : `/${url}`;
            store.add({
              contactId,
              name: img.name || "image",
              url: normalized,
            });
          }
        }
      };

      const req = index.openCursor(IDBKeyRange.only(contactId));
      req.onsuccess = deleteNext;
    });
  }

  /**
   * Get stored images for a contact. Returns [{ name, url }] for display in chat
   */
  async getImages(contactId) {
    if (!contactId) return [];
    const db = await this.ensureDB();
    const tx = db.transaction(["images"], "readonly");
    const store = tx.objectStore("images");
    const index = store.index("contactId");

    return new Promise((resolve, reject) => {
      const req = index.getAll(contactId);
      req.onsuccess = () => {
        const list = (req.result || []).map((item) => ({
          name: item.name,
          url: item.url,
          uploadedUrl: item.url,
        }));
        resolve(list);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Clear stored images for a contact (e.g. after send or remove all)
   */
  async clearImages(contactId) {
    if (!contactId) return;
    const db = await this.ensureDB();
    const tx = db.transaction(["images"], "readwrite");
    const store = tx.objectStore("images");
    const index = store.index("contactId");

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      const req = index.openCursor(IDBKeyRange.only(contactId));
      req.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
      };
    });
  }
}

const imageStorage = new ImageStorage();
export default imageStorage;
